import { guardGameSurface } from './186-game-surface.mjs';
import { skyFlightCapabilities } from '../../../packages/domain/src/106-sky-capabilities.mjs';
import { createSkyAimPad } from './166-sky-steering-pad.mjs';
import { installSkyTouchGuard } from './185-sky-touch-guard.mjs';

const clamp = (value, bound = 1) => Math.max(-bound, Math.min(bound, value));
const sensitivity = (value) => (Number.isFinite(value) ? Math.max(0.4, Math.min(2, value)) : 1);

/** Canvas input always aims; the independent pad owns movement pointers.
 * Touch is relative, mouse absolute, arrows aim and WASD steers in every phase.
 * Normalized sight survives camera/phase/pause changes. Clear releases held
 * actions only; recreate the owner to centre a new expedition's sight. */
export function createSkyFlightInput({
  e,
  surface,
  getCanvas,
  getState,
  canPlay,
  hasSequence,
  listen,
  onPause,
  onPulse,
  aimPoint,
  steering = () => ({ x: 0, y: 0 }),
  clearSteering = () => {},
  getSettings = () => ({}),
}) {
  const keys = new Set();
  const aimPad = e && surface ? createSkyAimPad({ e, surface }) : null;
  const attached = new WeakSet();
  let pointer = null,
    previous = '',
    sight = { x: 0, y: 0 },
    changed = false,
    lastPointerType = 'touch';
  const capability = () =>
    skyFlightCapabilities(getState(), { ready: canPlay(), sequence: hasSequence() });
  if (surface)
    installSkyTouchGuard({
      surface,
      listen,
      active: () => canPlay() && (getState().status === 'playing' || hasSequence()),
    });
  function clearPointer() {
    const target = pointer?.target,
      id = pointer?.id;
    pointer = null;
    aimPad?.clear();
    if (id !== undefined && target.hasPointerCapture(id)) target.releasePointerCapture(id);
  }
  function clear() {
    keys.clear();
    changed = false;
    clearPointer();
    clearSteering();
  }
  function syncMode() {
    const cap = capability();
    // Movement ownership changes cannot steal the aiming pointer during a
    // protected camera handoff. Axes/cruise require fresh movement input.
    const next = `${cap.manual.x}/${cap.manual.y}/${cap.manual.forward}`;
    if (previous && previous !== next) {
      for (const key of ['w', 'a', 's', 'd', 'r', 'f', ' ']) keys.delete(key);
      clearSteering();
    }
    previous = next;
    if (!cap.aim) clear();
    aimPad?.paint(cap.aim);
    return cap.aim;
  }
  function direct(event, canvas) {
    const rect = canvas.getBoundingClientRect(),
      settings = getSettings();
    const next = {
      x: clamp(
        (((event.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1) *
          (settings.aimInvertX ? -1 : 1),
        0.96,
      ),
      y: clamp(
        (1 - ((event.clientY - rect.top) / Math.max(1, rect.height)) * 2) *
          (settings.aimInvertY ? -1 : 1),
        0.96,
      ),
    };
    changed ||= next.x !== sight.x || next.y !== sight.y;
    sight = next;
  }
  function attachPointer(target, relative) {
    if (attached.has(target)) return;
    attached.add(target);
    listen(target, 'pointerdown', (event) => {
      syncMode();
      if (!capability().aim || pointer || event.button > 0) return;
      const canvas = getCanvas(),
        rect = canvas.getBoundingClientRect(),
        mirrored = getSettings().mirrored;
      // Canvas touches start in their assigned half. Captured gestures keep
      // their role across the midpoint, including those begun on the pad.
      if (
        !relative &&
        event.pointerType === 'touch' &&
        event.clientX < rect.left + rect.width / 2 !== Boolean(mirrored)
      )
        return;
      event.preventDefault();
      canvas.focus({ preventScroll: true });
      lastPointerType = event.pointerType ?? 'mouse';
      pointer = {
        id: event.pointerId,
        target,
        x: event.clientX,
        y: event.clientY,
        startX: event.clientX,
        startY: event.clientY,
      };
      if (!relative && event.pointerType !== 'touch') direct(event, canvas);
      if (relative) aimPad.move(0, 0);
      target.setPointerCapture(event.pointerId);
    });
    listen(target, 'pointermove', (event) => {
      syncMode();
      if (!capability().aim) return;
      if (!relative && event.pointerType === 'mouse') {
        if (pointer && pointer.target !== target) return;
        lastPointerType = 'mouse';
        direct(event, getCanvas());
        return;
      }
      if (pointer?.id !== event.pointerId || pointer.target !== target) return;
      event.preventDefault();
      const rect = getCanvas().getBoundingClientRect(),
        settings = getSettings();
      const scale =
        (3.2 * sensitivity(settings.aimSensitivity)) /
        Math.max(1, Math.min(rect.width, rect.height));
      const dx = event.clientX - pointer.x,
        dy = event.clientY - pointer.y;
      changed ||= dx !== 0 || dy !== 0;
      sight.x = clamp(sight.x + dx * scale * (settings.aimInvertX ? -1 : 1), 0.96);
      sight.y = clamp(sight.y - dy * scale * (settings.aimInvertY ? -1 : 1), 0.96);
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      if (relative) aimPad.move(event.clientX - pointer.startX, event.clientY - pointer.startY);
    });
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'])
      listen(target, name, (event) => {
        if (pointer?.id === event.pointerId && pointer.target === target) {
          clearPointer();
          changed = false;
        }
      });
    listen(target, 'contextmenu', (event) => event.preventDefault());
  }
  if (surface)
    guardGameSurface(surface, {
      listen,
      cancel: clear,
      ownsTouch: (event) =>
        Boolean(event.target?.closest?.('canvas, .sky-aim-pad, .sky-steering-pad')),
    });
  return {
    clear,
    reset() {
      clear();
      sight = { x: 0, y: 0 };
      previous = '';
    },
    syncMode,
    sample(dt) {
      syncMode();
      const cap = capability(),
        state = getState(),
        settings = getSettings();
      const down = (...names) => Number(names.some((name) => keys.has(name)));
      if (!cap.aim) return state.freeFlight ? { x: 0, y: 0, forward: 0 } : { x: 0, y: 0 };
      const axes = steering(),
        duration = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;
      const aimX = down('ArrowRight') - down('ArrowLeft'),
        aimY = down('ArrowUp') - down('ArrowDown');
      sight.x = clamp(
        sight.x +
          aimX *
            duration *
            1.4 *
            sensitivity(settings.aimSensitivity) *
            (settings.aimInvertX ? -1 : 1),
        0.96,
      );
      sight.y = clamp(
        sight.y +
          aimY *
            duration *
            1.4 *
            sensitivity(settings.aimSensitivity) *
            (settings.aimInvertY ? -1 : 1),
        0.96,
      );
      const active = changed || aimX !== 0 || aimY !== 0;
      const point = aimPoint(
        { ...sight },
        {
          active,
          dt: duration,
          assist: settings.assistance ?? (lastPointerType === 'mouse' ? 'light' : 'standard'),
        },
      );
      changed = false;
      return {
        x: cap.manual.x
          ? clamp(
              (down('d') - down('a')) *
                sensitivity(settings.moveSensitivity) *
                (settings.moveInvertX ? -1 : 1) +
                (axes.x || 0),
            )
          : 0,
        y: cap.manual.y
          ? clamp(
              (state.freeFlight ? down('r') - down('f') : down('w') - down('s')) *
                sensitivity(settings.moveSensitivity) *
                (settings.moveInvertY ? -1 : 1) +
                (axes.y || 0),
            )
          : 0,
        ...(state.freeFlight
          ? { forward: cap.manual.forward ? clamp(down('w') - down('s') + (axes.forward || 0)) : 0 }
          : {}),
        aimPoint: point,
      };
    },
    attach() {
      const canvas = getCanvas();
      attachPointer(canvas, false);
      if (aimPad) attachPointer(aimPad.element, true);
      listen(canvas, 'keydown', (event) => {
        const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
        if (
          ![
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'w',
            'a',
            's',
            'd',
            ...(getState().freeFlight ? ['r', 'f'] : []),
            ' ',
            'p',
            'Escape',
          ].includes(key)
        )
          return;
        event.preventDefault();
        if (key === 'p' || key === 'Escape') {
          if (event.repeat) return;
          clear();
          onPause();
          return;
        }
        syncMode();
        // OS key repeat is not a fresh press after pause, blur or scripted
        // movement ownership released the original held action.
        if (!capability().aim || (event.repeat && !keys.has(key))) return;
        keys.add(key);
        if (key.startsWith('Arrow')) lastPointerType = 'keyboard';
        if (key === ' ' && !event.repeat && capability().weapons) onPulse();
      });
      listen(canvas, 'keyup', (event) =>
        keys.delete(event.key.length === 1 ? event.key.toLowerCase() : event.key),
      );
      listen(canvas, 'blur', clear);
    },
  };
}
